/**
 * Shared types and constants for Spaces (Discord-like servers).
 *
 * Spaces have their own membership/channel/role model (separate from
 * conversations) so they can scale to arbitrary member counts. E2EE uses
 * Community Ciphers via a blind-relay verification challenge: the server
 * never stores Cipher entropy, derived keys, or cipherIds — only a
 * `CipherCheck` challenge. Content encryption is signaled by `e2ee`
 * (messages + structural metadata); `encryptIdentity` additionally encrypts
 * Space name/description for directory privacy; `cipherRequired` is a
 * join gate: the client proves it can decrypt the challenge (it never sends
 * the Cipher).
 *
 * @module api/spaces-types
 */
/** Space visibility levels. */
export const SPACE_VISIBILITY_VALUES = ['public', 'unlisted', 'hidden'];
/** Channel kinds. Voice channels are text channels plus A/V presence. */
export const SPACE_CHANNEL_TYPES = ['text', 'voice'];
/** Empty LiveKit room grace before teardown (seconds). */
export const SPACE_VOICE_EMPTY_GRACE_SEC = 60;
/** Membership states. */
export const SPACE_MEMBER_STATUSES = ['active', 'banned'];
/** Ban duration presets for Space moderation. */
export const SPACE_BAN_DURATIONS = ['1h', '1d', '7d', '30d', 'permanent'];
/** Viewer membership relative to a discover/directory Space card. */
export const SPACE_MEMBERSHIP_STATUSES = ['none', 'active', 'banned'];
/** Why a member left a Space (`space_member_left` socket payload). */
export const SPACE_MEMBER_LEFT_REASONS = ['left', 'kicked', 'banned'];
/** Member-list pages: extra-role holders first, then Everyone-only. */
export const SPACE_MEMBER_LIST_COHORTS = ['elevated', 'default'];
/** Invite lifecycle states (mirrors group invites, plus expiry). */
export const SPACE_INVITE_STATUSES = [
    'pending',
    'accepted',
    'declined',
    'revoked',
    'expired',
];
/** Invite link lifecycle states. */
export const SPACE_INVITE_LINK_STATUSES = ['active', 'revoked', 'expired'];
/** Space-scoped moderation / management audit actions. */
export const SPACE_AUDIT_ACTIONS = [
    'member_kick',
    'member_ban',
    'member_unban',
    'member_roles_update',
    'role_create',
    'role_update',
    'role_delete',
    'channel_acl_update',
    'message_mod_delete',
    'invite_create',
    'invite_revoke',
    'invite_link_create',
    'invite_link_revoke',
    'temporary_access_expired',
    'space_profile_update',
    'space_theme_update',
    'announcement_post',
    'announcement_edit',
    'announcement_delete',
    'activity_settings_update',
    'space_create',
    'space_settings_update',
    'channel_create',
    'channel_update',
    'channel_move',
    'channel_permissions_update',
    'category_create',
    'category_update',
    'category_delete',
    'category_permissions_update',
    'channel_layout_update',
    'role_reorder',
    'message_pin',
    'message_unpin',
    'member_join',
    'member_leave',
    'member_nickname_update',
    'invite_expired',
    'invite_link_expired',
    'member_note_create',
    'member_note_update',
    'member_note_delete',
];
export { SPACE_PERMISSIONS, SPACE_PERMISSION_SET, SPACE_PERMISSION_CATEGORIES, SPACE_ONLY_PERMISSION_CATEGORIES, SPACE_PERMISSION_DEFS, SPACE_MANAGE_UI_PERMISSIONS, DEFAULT_MEMBER_PERMISSIONS, DEFAULT_ADMIN_PERMISSIONS, DEFAULT_ADMIN_ROLE_COLOR, DEFAULT_MEMBER_ROLE_COLOR, DEFAULT_CUSTOM_ROLE_COLOR, normalizeSpacePermissions, spacePermissionListHas, canAccessSpaceManageUi, resolveSpaceInviteCapabilities, canDirectInviteIdentity, getSpacePermissionToggleValue, applySpacePermissionToggle, spacePermissionToggleOptions, spacePermissionsSubsetOf, canGrantSpaceMemberRole, resolveRolePermissionsThroughOverrides, findRolePermissionOverride, normalizeRolePermissionOverrides, SPACE_PROFILE_VIEW_PERMISSIONS, SPACE_PERMISSION_IMPLICATIONS, spacePermissionImpliedBy, } from './space-permissions';
export { DEFAULT_ADMIN_ROLE_NAME, DEFAULT_MEMBER_ROLE_NAME, DEFAULT_CUSTOM_ROLE_NAME, resolveSpaceRoleSystemKey, isSpaceAdminRole, isSpaceEveryoneRole, } from './space-system-roles';
export { partitionSpaceManageRoles, nextCustomRolePosition, packSpaceManageRolePositions, isExactCustomRolePermutation, } from './space-role-order';
// --- Shared field constraints (used by both client and server validation) ---
/** Slugs live at `/s/<slug>`: lowercase alphanumeric + internal hyphens. */
export const SPACE_SLUG_MIN_LENGTH = 3;
export const SPACE_SLUG_MAX_LENGTH = 32;
export const SPACE_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
/**
 * Slugs that cannot be claimed by a Space. Prevents collisions with existing
 * or future top-level app routes and confusing/impersonating names. Shared so
 * the create flow can surface a "reserved" state client-side and the API can
 * reject reserved slugs from a single source of truth. Compared
 * case-insensitively against the already-lowercased slug.
 */
export const SPACE_RESERVED_SLUGS = new Set([
    // App/route collisions
    's', 'space', 'spaces', 'api', 'app', 'www', 'admin', 'administrator',
    'settings', 'account', 'accounts', 'auth', 'login', 'logout', 'signup',
    'register', 'new', 'create', 'edit', 'delete', 'discover', 'explore',
    'directory', 'search', 'home', 'dashboard', 'help', 'support', 'about',
    'terms', 'privacy', 'legal', 'contact', 'billing', 'subscribe', 'upgrade',
    'pricing', 'invite', 'invites', 'join', 'me', 'you', 'user', 'users',
    'identity', 'identities', 'profile', 'profiles', 'notifications', 'messages',
    'conversations', 'friends', 'blocks', 'report', 'reports', 'moderation',
    'feedback', 'themes', 'emojis', 'uploads', 'media', 'cdn', 'assets',
    'static', 'public', 'private', 'null', 'undefined', 'true', 'false',
    // Brand / impersonation guards
    'adieuu', 'official', 'staff', 'system', 'root', 'mod', 'mods',
]);
/** Whether a slug is reserved and cannot be used for a Space (case-insensitive). */
export function isReservedSpaceSlug(slug) {
    return SPACE_RESERVED_SLUGS.has(slug.toLowerCase());
}
export const SPACE_NAME_MIN_LENGTH = 1;
export const SPACE_NAME_MAX_LENGTH = 100;
export const SPACE_DESCRIPTION_MAX_LENGTH = 500;
/** Max length for a role description (short enough to surface in tooltips). */
export const SPACE_ROLE_DESCRIPTION_MAX_LENGTH = 200;
/** True when this role was minted to bind an invite to specific channels. */
export function isInviteGeneratedRole(role) {
    return Boolean(role.generatedForInviteId);
}
export const SPACE_CHANNEL_NAME_MIN_LENGTH = 1;
export const SPACE_CHANNEL_NAME_MAX_LENGTH = 100;
/** The single text channel auto-created with every new Space. */
export const DEFAULT_SPACE_CHANNEL_NAME = 'general';
/** The root category auto-created with every new Space (holds `#general`). */
export const DEFAULT_SPACE_CATEGORY_NAME = 'Text Channels';
/** Max length for plaintext (non-E2EE) channel messages. */
export const SPACE_MESSAGE_MAX_LENGTH = 4000;
/** Max length for the base64-encoded ciphertext field in E2EE messages. */
export const SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH = 16384;
/** Max self-destruct timer for ephemeral channel messages (30 days). */
export const SPACE_MESSAGE_MAX_EXPIRES_SECONDS = 30 * 24 * 60 * 60;
/** Max attachments per Space channel message (cleartext or E2E), mirrors DM cap. */
export const SPACE_MESSAGE_MAX_ATTACHMENTS = 10;
/** Cap distinct reaction tags (HMAC or plaintext) stored on one Space message. */
export const SPACE_REACTION_MAX_DISTINCT_TAGS = 40;
/** HMAC-SHA256 hex digest length for encrypted Space reaction tags. */
export const SPACE_REACTION_TAG_HEX_LENGTH = 64;
/** Max message ids accepted by the batched "mine" Space reaction endpoint. */
export const SPACE_REACTION_MINE_BATCH_MAX = 50;
/** Max rows returned by one "who reacted" page. */
export const SPACE_REACTION_WHO_PAGE_MAX = 100;
const SPACE_REACTION_TAG_HEX_PATTERN = /^[0-9a-f]{64}$/;
/** True when `value` is a lowercase 64-char hex HMAC reaction tag. */
export function isSpaceReactionTagHex(value) {
    return SPACE_REACTION_TAG_HEX_PATTERN.test(value);
}
/** Max base64 length for an encrypted logo/banner pointer or theme. */
export const SPACE_IDENTITY_BLOB_CIPHERTEXT_MAX_LENGTH = 4096;
/** System role keys used when seeding encrypted role names at Space create. */
export const SPACE_SEED_ROLE_SYSTEMS = ['admin', 'everyone'];
/** Cap on identities returned in a Space invite hover preview. */
export const SPACE_INVITE_PREVIEW_MEMBER_LIMIT = 10;
/** Maximum nesting depth for Space channel categories (root = 1). */
export const SPACE_CATEGORY_MAX_DEPTH = 5;
// --- Voice channel sessions ---
export const SPACE_VOICE_SESSION_STATUSES = ['waiting', 'active', 'ended'];
// --- Durable unread + mention reconciliation (fan-out on read) ---
/**
 * Largest unread count the server will compute per channel. Counts are capped
 * so a member returning to a very active channel (or a huge community) never
 * triggers an unbounded scan; the client renders `SPACE_UNREAD_COUNT_CAP+` when
 * a channel reports the cap.
 */
export const SPACE_UNREAD_COUNT_CAP = 99;
//# sourceMappingURL=spaces-types.js.map